import { useId } from 'react';
import { MARK_PATHS, MARK_TONES, WORDMARK, type MarkTone } from './geometry';

/** Gradient defs + the three isotipo shapes, positioned in the 200×200 mark box. */
export function MarkShapes({ tone, idPrefix }: { tone: MarkTone; idPrefix: string }) {
  const fills = MARK_TONES[tone];
  return (
    <>
      <defs>
        <linearGradient id={`${idPrefix}-ring`} x1="0.12" y1="0.92" x2="0.9" y2="0.08">
          <stop offset="0" stopColor={fills.ring[0]} /><stop offset="0.55" stopColor={fills.ring[1]} /><stop offset="1" stopColor={fills.ring[2]} />
        </linearGradient>
        <linearGradient id={`${idPrefix}-top`} x1="0.1" y1="0.2" x2="0.95" y2="0.8">
          <stop offset="0" stopColor={fills.innerTop[0]} /><stop offset="1" stopColor={fills.innerTop[1]} />
        </linearGradient>
        <linearGradient id={`${idPrefix}-bottom`} x1="0.05" y1="0.9" x2="1" y2="0.2">
          <stop offset="0" stopColor={fills.innerBottom[0]} /><stop offset="1" stopColor={fills.innerBottom[1]} />
        </linearGradient>
      </defs>
      <path d={MARK_PATHS.ring} fill={`url(#${idPrefix}-ring)`} />
      <path d={MARK_PATHS.innerTop} fill={`url(#${idPrefix}-top)`} />
      <path d={MARK_PATHS.innerBottom} fill={`url(#${idPrefix}-bottom)`} />
    </>
  );
}

/** Wordmark text set in the brand display face; spacing locked by textLength so fallback fonts keep the lockup. */
function Wordmark({ tone, x, y, size, width, anchor = 'start' }: { tone: MarkTone; x: number; y: number; size: number; width: number; anchor?: 'start' | 'middle' }) {
  const fills = MARK_TONES[tone];
  return (
    <text x={x} y={y} fontFamily="'Montserrat Variable', Montserrat, sans-serif" fontWeight={780} fontSize={size}
      textLength={width} lengthAdjust="spacing" textAnchor={anchor} letterSpacing="-0.5">
      <tspan fill={fills.primary}>{WORDMARK.primary}</tspan><tspan fill={fills.secondary}>{WORDMARK.secondary}</tspan>
    </text>
  );
}
function Descriptor({ tone, x, y, size, width, anchor = 'start' }: { tone: MarkTone; x: number; y: number; size: number; width: number; anchor?: 'start' | 'middle' }) {
  return (
    <text x={x} y={y} fontFamily="'Montserrat Variable', Montserrat, sans-serif" fontWeight={520} fontSize={size}
      textLength={width} lengthAdjust="spacing" textAnchor={anchor} fill={MARK_TONES[tone].descriptor}>
      {WORDMARK.descriptor}
    </text>
  );
}

export type MarkLayout = 'mark' | 'horizontal' | 'stacked';
/**
 * Static COLESCOMEX identity. `label` gives the accessible name; decorative uses pass `label={null}`.
 * Layout geometry is fixed: never scale axes independently (preserveAspectRatio default).
 */
export function BrandMark({ layout = 'horizontal', tone = 'light', descriptor = false, label = 'COLESCOMEX', className }:
  { layout?: MarkLayout; tone?: MarkTone; descriptor?: boolean; label?: string | null; className?: string }) {
  const idPrefix = `cxm${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const a11y = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true as const, focusable: false as const };
  if (layout === 'mark') {
    return <svg className={className} viewBox="0 0 200 200" {...a11y}><MarkShapes tone={tone} idPrefix={idPrefix} /></svg>;
  }
  if (layout === 'stacked') {
    return (
      <svg className={className} viewBox={descriptor ? '0 0 760 420' : '0 0 760 360'} {...a11y}>
        <g transform="translate(290 0) scale(0.9)"><MarkShapes tone={tone} idPrefix={idPrefix} /></g>
        <Wordmark tone={tone} x={380} y={330} size={112} width={740} anchor="middle" />
        {descriptor && <Descriptor tone={tone} x={380} y={398} size={25} width={730} anchor="middle" />}
      </svg>
    );
  }
  return (
    <svg className={className} viewBox={descriptor ? '0 0 920 200' : '0 0 920 200'} {...a11y}>
      <MarkShapes tone={tone} idPrefix={idPrefix} />
      <Wordmark tone={tone} x={236} y={descriptor ? 118 : 138} size={92} width={676} />
      {descriptor && <Descriptor tone={tone} x={238} y={170} size={23} width={672} />}
    </svg>
  );
}
